/**
 * Serviço de Cache Premium - Evita consultas desnecessárias ao backend
 * Implementa padrões de mercado: TTL, Hash diário, Validação automática
 */
export class PremiumCacheService {
  
  constructor() {
    this.CACHE_PREFIX = 'equilibra_premium_'
    this.TTL_HOURS = 24 // Cache válido por 24 horas
  }

  /**
   * Gera chave única baseada em usuário + data
   * @param {string} userId - ID do usuário
   * @returns {string} Chave única do cache
   */
  _generateCacheKey(userId) {
    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD
    const hash = btoa(`${userId}_${today}`).replace(/[^a-zA-Z0-9]/g, '')
    return `${this.CACHE_PREFIX}${hash}`
  }

  /**
   * Verifica se cache é válido
   * @param {Object} cacheData - Dados do cache
   * @returns {boolean} Se cache é válido
   */
  _isCacheValid(cacheData) {
    if (!cacheData || !cacheData.timestamp) return false
    
    // Verifica integridade do cache
    if (!this._verifyChecksum(cacheData)) {
      console.warn('⚠️ Cache premium com integridade comprometida')
      return false
    }
    
    const now = Date.now()
    const cacheAge = now - cacheData.timestamp
    const maxAge = this.TTL_HOURS * 60 * 60 * 1000 // 24h em ms
    
    // Cache expirou por tempo
    if (cacheAge > maxAge) return false
    
    // Se premium tem data de expiração, verifica se ainda é válido
    if (cacheData.data.premiumExpires) {
      const expirationDate = new Date(cacheData.data.premiumExpires)
      if (expirationDate < new Date()) {
        return false // Premium expirou
      }
    }
    
    return true
  }

  /**
   * Gera checksum para validar integridade
   * @param {Object} data - Dados do premium
   * @param {string} userId - ID do usuário
   * @returns {string} Checksum
   */
  _generateChecksum(data, userId) {
    const str = JSON.stringify(data) + userId + 'equilibra_salt_2025'
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    return hash.toString(36)
  }

  /**
   * Verifica checksum do cache
   * @param {Object} cacheData - Dados do cache
   * @returns {boolean} Se checksum é válido
   */
  _verifyChecksum(cacheData) {
    if (!cacheData.checksum || !cacheData.data || !cacheData.userId) {
      return false
    }
    
    const expectedChecksum = this._generateChecksum(cacheData.data, cacheData.userId)
    return cacheData.checksum === expectedChecksum
  }

  /**
   * Obtém status premium do cache
   * @param {string} userId - ID do usuário
   * @returns {Object|null} Dados do premium ou null se não cached/inválido
   */
  getPremiumStatus(userId) {
    try {
      const cacheKey = this._generateCacheKey(userId)
      const cached = localStorage.getItem(cacheKey)
      
      if (!cached) return null
      
      const cacheData = JSON.parse(cached)
      
      if (!this._isCacheValid(cacheData)) {
        // Remove cache inválido
        this.clearPremiumCache(userId)
        return null
      }
      return cacheData.data
      
    } catch (error) {
      console.error('Erro ao obter premium do cache:', error)
      return null
    }
  }

  /**
   * Armazena status premium no cache
   * @param {string} userId - ID do usuário
   * @param {Object} premiumData - Dados do premium
   */
  setPremiumStatus(userId, premiumData) {
    try {
      const cacheKey = this._generateCacheKey(userId)
      const cacheData = {
        data: premiumData,
        timestamp: Date.now(),
        userId: userId,
        version: '1.0',
        checksum: this._generateChecksum(premiumData, userId)
      }
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData))
      
    } catch (error) {
      console.error('Erro ao salvar premium no cache:', error)
    }
  }

  /**
   * Limpa cache premium do usuário
   * @param {string} userId - ID do usuário
   */
  clearPremiumCache(userId) {
    try {
      const cacheKey = this._generateCacheKey(userId)
      localStorage.removeItem(cacheKey)
      console.log('🗑️ Cache premium limpo para usuário:', userId)
    } catch (error) {
      console.error('Erro ao limpar cache premium:', error)
    }
  }

  /**
   * Limpa todos os caches premium expirados
   */
  cleanExpiredCaches() {
    try {
      const keys = Object.keys(localStorage)
      const premiumKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX))
      
      let cleaned = 0
      premiumKeys.forEach(key => {
        try {
          const cached = JSON.parse(localStorage.getItem(key))
          if (!this._isCacheValid(cached)) {
            localStorage.removeItem(key)
            cleaned++
          }
        } catch (error) {
          // Remove cache corrompido
          localStorage.removeItem(key)
          cleaned++
        }
      })
      
      if (cleaned > 0) {
        console.log(`🧹 ${cleaned} caches premium expirados removidos`)
      }
      
    } catch (error) {
      console.error('Erro ao limpar caches expirados:', error)
    }
  }

  /**
   * Força atualização do cache (próxima consulta irá ao backend)
   * @param {string} userId - ID do usuário
   */
  invalidateCache(userId) {
    this.clearPremiumCache(userId)
    console.log('🔄 Cache premium invalidado - próxima consulta irá ao backend')
  }

  /**
   * Obtém estatísticas do cache
   * @returns {Object} Estatísticas do cache
   */
  getCacheStats() {
    try {
      const keys = Object.keys(localStorage)
      const premiumKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX))
      
      let valid = 0
      let expired = 0
      
      premiumKeys.forEach(key => {
        try {
          const cached = JSON.parse(localStorage.getItem(key))
          if (this._isCacheValid(cached)) {
            valid++
          } else {
            expired++
          }
        } catch (error) {
          expired++
        }
      })
      
      return {
        total: premiumKeys.length,
        valid,
        expired,
        hitRate: valid > 0 ? ((valid / (valid + expired)) * 100).toFixed(1) : 0
      }
      
    } catch (error) {
      console.error('Erro ao obter estatísticas do cache:', error)
      return { total: 0, valid: 0, expired: 0, hitRate: 0 }
    }
  }
}

// Singleton instance
export const premiumCache = new PremiumCacheService()

// Auto-limpeza a cada 1 hora
setInterval(() => {
  premiumCache.cleanExpiredCaches()
}, 60 * 60 * 1000)